import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Section from '@/components/Section';
import ShoppingCart from '@/components/ShoppingCart';
import ShoppingCartFooterNew from '@/components/ShoppingCart/ShoppingCartFooterNew';
import Sticky from 'react-stickynode';
import { API_STATUS, LAYOUT_TYPES, SCREEN_TYPES, SHOPPING_CART_FOOTER_LAYOUTS } from '@/utils/constants';
import { getPrices } from '@/utils/index';
import CallOut from '@/components/CallOut';
import EmployeeModal from "@/components/Modals/EmployeeModal";
import { actions as modalActions } from '@gc-npm/reducers.modals-reducer';

const ConfiguradorLoadingFake = () => (
  <>
    <div className="sections">
      {[4, 1, 3, 5].map((fakeCardsNumber, sectionIndex) => (
        <section className="fakeSection" key={`fake-section-${sectionIndex}`}>
          <div className="fakeSectionTitle skeleton" />
          <div className={`fakeCards ${sectionIndex === 0 ? 'fakeCardsCarousel' : ''}`}>
            {Array.from({ length: fakeCardsNumber }, (_, cardIndex) => (
              <div
                className="fakeCard skeleton"
                key={`fake-card-${sectionIndex}-${cardIndex}`}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
    <aside className="fakeShoppingCart skeleton" />
    <style jsx>
      {`
        .sections {
          display: flex;
          flex-direction: column;
          gap: 2rem;
          margin-top: 1.5rem;
        }
        .fakeSection {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-right: 1rem;
        }
        .fakeSectionTitle {
          width: 260px;
          height: 32px;
          border-radius: 8px;
        }
        .fakeCards {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .fakeCardsCarousel {
          flex-wrap: nowrap;
          overflow: hidden;
        }
        .fakeCard {
          width: 288px;
          min-width: 288px;
          max-width: 288px;
          flex: 0 0 288px;
          height: 223px;
          min-height: 223px;
          border-radius: 8px;
        }
        .fakeShoppingCart {
          width: 288px;
          min-height: 360px;
          border-radius: 1rem;
        }
        @media screen and (min-width: 1535px) {
          .fakeCard {
            width: 322px;
            min-width: 322px;
            max-width: 322px;
            flex-basis: 322px;
          }
          .fakeShoppingCart {
            width: 360px;
          }
        }
        @media screen and (max-width: 768px) {
          .fakeSection {
            margin-right: 0;
          }
          .fakeSectionTitle {
            width: 70%;
          }
          .fakeCard {
            width: calc(50% - 0.5rem);
            min-width: 160px;
            max-width: calc(50% - 0.5rem);
            flex-basis: calc(50% - 0.5rem);
          }
          .fakeShoppingCart {
            display: none;
          }
        }
        @media screen and (max-width: 350px) {
          .fakeCard {
            width: 100%;
            min-width: 100%;
            max-width: 100%;
            flex-basis: 100%;
          }
        }
      `}
    </style>
  </>
);

const EMPLOYEE_MODAL_PROPS = {
  closeByClickingOutside: false,
  contentPosition: "center--center",
  inSceneEffect: "fadeIn",
  outSceneEffect: "fadeOut",
  opacityLayoutColor: "black05",
  scrollAllowed: false,
  historyBackOnClose: false,
};

export const Configurador = () => {
  const dispatch = useDispatch();
  const employeeModalOpen = useRef(false);
  const modulos = useSelector((state) => state.modulos);
  const cart = useSelector((state) => state.cart);
  const screenType = useSelector((state) => state.UI.screenType);
  const layout = useSelector((state) => state.UI.layout);
  const callOut = useSelector((state) => state.UI.callOut);
  const customer = useSelector((state) => state.main?.customer);
  const prices = getPrices(layout, cart);

  useEffect(() => {
    if (!employeeModalOpen.current && customer?.groups?.some((g) => g?.customerGroupId === "1")) {
      dispatch(modalActions.addModal({
        id: "device_modal",
        Element2Show: EmployeeModal,
        modalProps: { ...EMPLOYEE_MODAL_PROPS },
        elementProps: {
          title: { text: "Tienes un producto para empleados de Movistar" },
          description: { text: "Si quieres modificar tu pack miMovistar puedes hacerlo en la página de empleados. Te redirigimos para que veas todas las opciones disponibles para ti." },
          button: { label: "Ver oferta de empleados", href: "https://www.movistar.es/empleados" },
          baseTaggingInfo: {
            componentName: "device_modal",
            componentContent: "cerrar",
            componentProduct: "autonomos-config-modal",
            componentVariation: "all",
          },
        },
      }));
      employeeModalOpen.current = true;
    }
  }, [customer]);
  const isLoadingModulos = modulos?.statusModulos === API_STATUS.LOADING;
  const hasConfiguradorSections = Boolean(modulos?.conectividad || modulos?.tv);
  const shouldShowConfigurador =
    modulos?.statusModulos === API_STATUS.OK ||
    isLoadingModulos;

  const shoppingCartModules = cart?.modulos ? { ...cart.modulos } : null;
  if (shoppingCartModules) {
    if (layout === LAYOUT_TYPES.CONECTIVIDAD) {
      shoppingCartModules.tv = null;
    } else if (layout === LAYOUT_TYPES.TV) {
      shoppingCartModules.conectividad = null;
    }
  }

  const shoppingCartProps = {
    screenType,
    ...cart,
    modulos: shoppingCartModules,
    prices,
  };

  return (
    <>
      <main className="Configurador container">
        {isLoadingModulos && !hasConfiguradorSections && (
          <ConfiguradorLoadingFake />
        )}
        {modulos && hasConfiguradorSections && shouldShowConfigurador && (
          <>
            <div className="sections">
              {callOut && (
                <CallOut {...callOut} />
              )}
              {(layout === LAYOUT_TYPES.ALL || layout === LAYOUT_TYPES.CONECTIVIDAD) && modulos.conectividad && (
                <Section
                  alias="cms-page-tv-config-1"
                  blockName="conectividad"
                  {...modulos.conectividad}
                />
              )}
              {(layout === LAYOUT_TYPES.ALL || layout === LAYOUT_TYPES.TV) && modulos.tv && (
                <Section
                  alias="cms-page-tv-config-1"
                  blockName="tv"
                  {...modulos.tv}
                />
              )}
            </div>
            <div className="shoppingCart">
              {screenType === SCREEN_TYPES.MOBILE ? (
                <ShoppingCartFooterNew
                  layoutType={SHOPPING_CART_FOOTER_LAYOUTS.CONFIG_MOBILE}
                  {...shoppingCartProps}
                  taggingInfoName="shoppingCart"
                />
              ) : (
                <Sticky enabled top={110} bottomBoundary=".finalSticky">
                  <ShoppingCart {...shoppingCartProps} />
                </Sticky>
              )}
            </div>
          </>
        )}
      </main>
      <style jsx>
        {`
          .Configurador {
            display: grid;
            grid-template-columns: 75% auto;
            justify-content: space-between;
            min-height: 100vh;
          }
          .sections {
            display: flex;
            flex-direction: column;
            gap: 2rem;
            margin-top:1.5rem;
            
          }
          .legals {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
          }
          .shoppingCart {
            width: 260px;
          }
          :global(.legals__title) {
            padding-right:0.4rem;
          }
          @media screen and (min-width: 1279px) {
            .shoppingCart {
              width: 288px;
            }
          }
          @media screen and (min-width: 1535px) {
            .shoppingCart {
              width: 360px;
            }
          }
          @media screen and (max-width: 768px) {
            .Configurador {
              grid-template-columns: 100%;
            }
            .shoppingCart {
              width: 100%;
            }
          }
        `}
      </style>
    </>
  );
};

export default Configurador;
